import { Playbook, PlaybookReportType } from '@driftt/api-types'
import { A, Button, Div } from '@driftt/tide-core'
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalSidebar,
  ModalSplitView,
  ModalTitle,
} from '@driftt/tide-modal'
import cx from 'classnames'
import React, { useState } from 'react'

import { isFastLanePlaybook } from 'utils/playbooks'
import {
  trackHowToBuildABotInsiderLinkClicked,
  trackHowToBuildABotTypeToggled,
} from 'utils/tracking'

interface Props {
  onClose: () => void
  open: boolean
  onCancel: () => void
  draftPlaybook: Playbook
}

interface EduItem {
  label: string
  src: string
  title: string
  insiderLink: string
  insiderCourseName: string
}

export enum BotType {
  ENGAGE_ALL = 'ENGAGE_ALL',
  PRICING_PAGE = 'PRICING_PAGE',
  TARGET_ACCOUNT = 'TARGET_ACCOUNT',
  PAID_AD_CONVERTER = 'PAID_AD_CONVERTER',
  VISITOR_RETARGETING = 'VISITOR_RETARGETING',
  FASTLANE = 'FASTLANE',
}

const CHAT_BOT_TYPES = [
  BotType.ENGAGE_ALL,
  BotType.PRICING_PAGE,
  BotType.TARGET_ACCOUNT,
  BotType.PAID_AD_CONVERTER,
  BotType.VISITOR_RETARGETING,
]

const botTypeToEduMap: { [key: string]: EduItem } = {
  [BotType.ENGAGE_ALL]: {
    label: 'Engage all',
    src: 'https://fast.wistia.net/embed/iframe/82m9jwegz2?videoFoam=true',
    title:
      'Playbook Basics Certification_Engage All Playbook - Building Your Bot in Drift Video',
    insiderLink:
      'https://insider.drift.com/4012284/stage-1-playbooks-to-engage-website-visitors?tpId=108705',
    insiderCourseName: 'Stage 1',
  },
  [BotType.PRICING_PAGE]: {
    label: 'Pricing page',
    src: 'https://fast.wistia.net/embed/iframe/azvg1xlbiw?videoFoam=true',
    title: 'Playbook Basics Certification_The Pricing Page Playbook Video',
    insiderLink:
      'https://insider.drift.com/4012284/stage-1-playbooks-to-engage-website-visitors?tpId=108705',
    insiderCourseName: 'Stage 1',
  },
  [BotType.FASTLANE]: {
    label: 'Fastlane',
    src: 'https://fast.wistia.net/embed/iframe/i1vgpj7yyk?videoFoam=true',
    title: 'Fastlane Video',
    insiderLink: 'https://insider.drift.com/4289611/drift-training%3A-fastlane',
    insiderCourseName: 'Fastlane',
  },
  [BotType.TARGET_ACCOUNT]: {
    label: 'Target accounts',
    src: 'https://fast.wistia.net/embed/iframe/yji80jk97e?videoFoam=true',
    title: 'Playbook Basics Certification_Target Account Experiences Video',
    insiderLink:
      'https://insider.drift.com/4002917/stage-2-playbooks-to-target-potential-buyers?tpId=108705',
    insiderCourseName: 'Stage 2',
  },
  [BotType.PAID_AD_CONVERTER]: {
    label: 'Paid ad converter',
    src: 'https://fast.wistia.net/embed/iframe/rlxo1ut4vk?videoFoam=true',
    title: 'Playbook Basics Certification_Paid Ad Converter Video',
    insiderLink:
      'https://insider.drift.com/4002917/stage-2-playbooks-to-target-potential-buyers?tpId=108705',
    insiderCourseName: 'Stage 2',
  },
  [BotType.VISITOR_RETARGETING]: {
    label: 'Visitor retargeting',
    src: 'https://fast.wistia.net/embed/iframe/brxom7m367?videoFoam=true',
    title: 'Playbook Basics Certification_Visitor Retargeting Playbook Video',
    insiderLink:
      'https://insider.drift.com/4012284/stage-1-playbooks-to-engage-website-visitors?tpId=108705',
    insiderCourseName: 'Stage 1',
  },
}

const reportTypeToBotTypeMap: {
  [key: string]: BotType
} = {
  [PlaybookReportType.CONVERSATION_STARTER_BOT]: BotType.ENGAGE_ALL,
  [PlaybookReportType.ENGAGE_ALL_BOT]: BotType.ENGAGE_ALL,
  [PlaybookReportType.PRICING_PAGE_BOT]: BotType.PRICING_PAGE,
  [PlaybookReportType.ABM_HOMEPAGE_BOT]: BotType.TARGET_ACCOUNT,
  [PlaybookReportType.TARGET_ACCOUNTS_BOT]: BotType.TARGET_ACCOUNT,
  [PlaybookReportType.PAID_TRAFFIC_BOT]: BotType.PAID_AD_CONVERTER,
  [PlaybookReportType.PAID_AD_CONVERTER_BOT]: BotType.PAID_AD_CONVERTER,
  [PlaybookReportType.RETARGETING_BOT]: BotType.VISITOR_RETARGETING,
  [PlaybookReportType.VISITOR_RETARGETING_BOT]: BotType.VISITOR_RETARGETING,
}

const BuildABotModal = ({ open, onClose, draftPlaybook }: Props) => {
  const isFastlane = isFastLanePlaybook(draftPlaybook)
  const initialBotType = isFastlane
    ? BotType.FASTLANE
    : reportTypeToBotTypeMap[draftPlaybook.reportType] ?? BotType.ENGAGE_ALL
  const [selectedBotType, setSelectedBotType] = useState(initialBotType)

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="large"
      className="build-a-bot-modal"
    >
      <ModalSplitView
        className={cx('build-a-bot-modal-splitview', {
          'build-a-bot-modal--fastlane-mode': isFastlane,
        })}
      >
        {!isFastlane && (
          <>
            <ModalTitle className="build-a-bot-modal-title">
              How to build a bot
            </ModalTitle>
            <ModalBody>
              <Div className="build-a-bot-modal-subnav">
                {CHAT_BOT_TYPES.map((botType) => (
                  <Div
                    className={cx('build-a-bot-modal-subnav-item', {
                      'is-active': selectedBotType === botType,
                    })}
                    onClick={() => {
                      setSelectedBotType(botType)
                      trackHowToBuildABotTypeToggled(botType)
                    }}
                  >
                    {botTypeToEduMap[botType].label}
                  </Div>
                ))}
              </Div>
            </ModalBody>
          </>
        )}
        <ModalSidebar className="build-a-bot-modal-sidebar">
          {isFastlane && (
            <ModalTitle className="build-a-bot-modal-title">
              How to build a fastlane bot
            </ModalTitle>
          )}
          <div className="build-a-bot-video-iframe">
            <iframe
              src={botTypeToEduMap[selectedBotType].src}
              title={botTypeToEduMap[selectedBotType].title}
              allow="autoplay; fullscreen"
              frameBorder="0"
              scrolling="no"
              className="wistia_embed"
              name="wistia_embed"
              width="100%"
              height="100%"
            ></iframe>
          </div>
          <script
            src="https://fast.wistia.net/assets/external/E-v1.js"
            async
          ></script>
          <Div
            className="build-a-bot-modal-link"
            onClick={() =>
              trackHowToBuildABotInsiderLinkClicked(selectedBotType)
            }
          >
            <A
              href={botTypeToEduMap[selectedBotType].insiderLink}
              target="_blank"
            >
              View the full {botTypeToEduMap[selectedBotType].insiderCourseName}{' '}
              class in Drift Insider+
            </A>
          </Div>
        </ModalSidebar>
      </ModalSplitView>
      <ModalFooter>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  )
}

export default BuildABotModal
